import { useTranslations } from "@cirrus/core/i18n"

import React, { ReactElement, useCallback, useState } from "react"
import Draggable from "react-draggable"
import { ResizableBox } from "react-resizable"
import "react-resizable/css/styles.css"

import LockOutlinedIcon from "@mui/icons-material/LockOutlined"
import ReportGmailErrorOutlinedIcon from "@mui/icons-material/ReportGmailerrorredOutlined"
import WarningAmberIcon from "@mui/icons-material/WarningAmber"
import { DialogTitle, DialogTitleProps, IconButton, Paper, PaperProps, styled } from "@mui/material"
import { backdropClasses } from "@mui/material/Backdrop"
import Box, { BoxProps } from "@mui/material/Box"
import MuiDialog, { dialogClasses, DialogProps } from "@mui/material/Dialog"
import Typography, { TypographyProps } from "@mui/material/Typography"

import { MuiIcon } from "@cirrus/ui-kit-ts"
import { RoundedActionButton, RoundedActionButtonProps } from "@cirrus-delivery/v2/ui/RoundedActionButton"

import { DIALOG_TITLE_ID } from "./DialogTitleId"

const DraggableDialog = styled(MuiDialog)(() => ({
  margin: "0 auto",
  [`& .${backdropClasses.root}`]: {
    backgroundColor: "rgba(0, 0, 0, 0.3)"
  },
  [`& .${dialogClasses.paper}`]: {
    display: "flex",
    flexDirection: "column",
    gap: ".8rem",
    margin: 0,
    maxWidth: "none",
    boxShadow: "0px 1px 20px 0px #0000001A",
    position: "relative",
  }
}))

const ResizeHandle = styled("div")(({ theme }) => ({
  position: "absolute",
  width: "20px",
  height: "20px",
  bottom: "-0.5rem",
  cursor: "se-resize",
  [theme.breakpoints.up("sm")]: {
    right: "calc(-2rem + 3px)",
  },
  [theme.breakpoints.down("sm")]: {
    right: "calc(-1rem + 3px)",
  },
  "&::after": {
    content: '""',
    position: "absolute",
    right: "-0.1rem",
    bottom: "-0.1rem",
    width: "12px",
    height: "12px",
    borderRight: "2px solid rgba(0, 0, 0, 0.4)",
    borderBottom: "2px solid rgba(0, 0, 0, 0.4)"
  }
}))

export function DraggableDeliveryDialog({
    onClose,
    noPadding,
    resizable,
    resizableInitialWidth,
    resizableMinWidth,
    resizableMinHeight,
    header,
    ...props
  }: DeliveryDialogDraggableProps) {
  const [size, setSize] = useState({
    width: window.innerWidth >= 600 ? resizableInitialWidth?.sm || 700 : resizableInitialWidth?.xs || 232,
    height: 210
  })
  const { i18n } = useTranslations()

  const onCloseHandler = (_, reason: string) => {
    if (reason === "backdropClick") {
      return
    }

    onClose?.()
  }

  const getMaxConstraint = (value) => value - 70

  const onResize = useCallback((_, { size: newSize }) => {
    requestAnimationFrame(() => {
      setSize({
        width: newSize.width,
        height: newSize.height
      })
    })
  }, [])

  const content = (
    <>
      {
        header
        ||
        <Title
          id={DIALOG_TITLE_ID}
          sx={{
            cursor: "move",
            padding: noPadding ? {
              xs: ".8rem 1rem 0 1rem",
              sm: ".8rem 2rem 0 2rem"
            } : 0
          }}
        >
          <Box>{props.title || ""}</Box>
        </Title>
      }

      {
        onClose
        &&
        <IconButton onClick={onClose}
                    aria-label={i18n("General:Close")}
                    sx={{ position: "absolute", right: 0, top: 0 }}>
          <MuiIcon.Close sx={{ fontSize: "1.8rem" }} />
        </IconButton>
      }

      <Box sx={{ height: "100%" }}>{props.children}</Box>
    </>
  )

  return (
    <DraggableDialog
      hideBackdrop
      maxWidth={false}
      PaperComponent={DraggablePaper}
      aria-labelledby={DIALOG_TITLE_ID}
      PaperProps={{
        sx: {
          padding: noPadding ? 0 : {
            xs: ".8rem 1rem",
            sm: ".8rem 2rem"
          },
          width: !resizable ? "100%" : undefined
        }
      }}
      onClose={onCloseHandler}
      {...props}
      title={undefined}
    >
      {resizable ? (
        <ResizableBox
          width={size.width}
          height={size.height}
          onResize={onResize}
          minConstraints={[resizableMinWidth || 232, resizableMinHeight || 180]}
          maxConstraints={[
            getMaxConstraint(window.innerWidth),
            getMaxConstraint(window.innerHeight),
          ]}
          resizeHandles={["se"]}
          handle={<ResizeHandle className="react-resizable-handle react-resizable-handle-se" />}
        >
          <Box sx={{
            width: "100%",
            height: "100%",
            position: "relative"
          }}>
            {content}
          </Box>
        </ResizableBox>
      ) : content}
    </DraggableDialog>
  )
}

export function DeliveryDialog(props: DeliveryDialogProps) {
  return <Dialog PaperProps={{ elevation: 0 }} {...props} aria-labelledby={DIALOG_TITLE_ID} />
}

export function DeliveryContentDialog(props: DeliveryDialogProps) {
  return <ContentDialog PaperProps={{ elevation: 0 }} {...props} />
}

type DeliveryDialogDraggableProps = DialogProps & {
  noPadding?: boolean
  resizable?: boolean
  resizableInitialWidth?: {
    xs: number
    sm: number
  }
  resizableMinWidth?: number
  resizableMinHeight?: number
  onClose?: () => void
  header?: ReactElement
}

export type DeliveryDialogProps = DialogProps

const DraggablePaper = (props: PaperProps) => {
  // @ts-ignore
  return <Draggable
    handle={`#${DIALOG_TITLE_ID}`}
    cancel={"[class*=MuiDialogContent-root]"}>
    <Paper {...props} />
  </Draggable>
}

interface TitleProps extends DialogTitleProps {
  noPadding?: boolean
}

export const Title = styled(DialogTitle)<TitleProps>(() => ({
  lineHeight: "3.2rem",
  fontSize: "1.6rem",
  fontWeight: 400,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  placeContent: "center"
}))

const Dialog = styled(MuiDialog)(({ }) => ({
  [`& .${backdropClasses.root}`]: {
    backgroundColor: "rgba(0, 0, 0, 0.3)"
  },
  [`& .${dialogClasses.paper}`]: {
    width: "31.2rem",
    padding: "2.4rem",
    display: "grid",
    justifyItems: "center",
    rowGap: "1.6rem",
    boxShadow: "0px 1px 20px 0px #0000001A",
  }
}))

const ContentDialog = styled(MuiDialog)(() => ({
  [`& .${backdropClasses.root}`]: {
    backgroundColor: "rgba(0, 0, 0, 0.3)"
  },
  [`& .${dialogClasses.paper}`]: {
    width: "80rem",
    padding: "2.4rem",
    display: "grid",
    rowGap: "1.6rem",
    boxShadow: "0px 1px 20px 0px #0000001A",
  }
}))

DeliveryDialog.Title = (props: TypographyProps) => <Typography
  variant="h2"
  fontWeight={400}
  fontSize="2.4rem"
  lineHeight="3.2rem"
  align="center"
  id={DIALOG_TITLE_ID}
  {...props}
/>

DeliveryDialog.Content = ({ sx, ...props }: BoxProps) =>
  <Box sx={{ width: "100%", ...sx }} {...props} />

DeliveryDialog.Actions = ({ sx, ...props }: BoxProps) => <Box sx={{
  display: "flex",
  columnGap: "2.4rem",
  justifySelf: "end",
  paddingTop: "calc(2.4rem - 1.6rem)",
  ...sx
}} {...props} />

DeliveryDialog.ActionButton = (props: RoundedActionButtonProps) =>
  <RoundedActionButton color="inherit" {...props} />

const iconSx = { fontSize: "2.4rem" }

DeliveryDialog.WarningIcon = () => <WarningAmberIcon color="error" sx={iconSx} />
DeliveryDialog.LockIcon = () => <LockOutlinedIcon color="success" sx={iconSx} />
DeliveryDialog.ReportIcon = () => <ReportGmailErrorOutlinedIcon color="success" sx={iconSx} />
